import { Button, TextField } from '@mui/material';
import { deleteField } from 'firebase/firestore';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FirestoreService } from '../../../common/firebase/firestore.service';
import { Point3 } from '../../../common/models/point.type';
import { severity } from '../../../common/models/severity.enum';
import { Site } from '../../../confi-state';
import { ServiceManager } from '../../../services/service-manager';
import PdfPreview from '../../PdfPreview/PdfPreview';
import styles from './UnderlayEdit.module.css';

interface UnderlayEditProps {
    currentSite: Site,
    editingUnderlay: boolean,
    onChangeSite: (projectId: number | string, site: Site) => void,
    setEditingUnderlay: (editing: boolean) => void
}

const saveFileAndUnderlayData = (currentSite: Site, onChangeSite, defaultPosition: Point3, defaultRotation: Point3, extension: string, siteUnderlayImage: File, width: number, height: number, scale: number) => {
    let path = `user-data/${currentSite.userId}/${currentSite.projectId}/${currentSite.id}/underlay.${extension}`;
    FirestoreService.uploadFile(
        path,
        siteUnderlayImage,
        { contentType: siteUnderlayImage.type }
    ).then(() => {
        FirestoreService.getDownloadUrl(path).then(res => {
            onChangeSite(currentSite.projectId,
                {
                    ...currentSite,
                    underlayImage: { link: res, path, position: defaultPosition, rotation: defaultRotation, scale, width, height }
                }
            );
        }).catch(e => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens ophalen van download URL: "${e.message}"`, severity.error);
        });
    }).catch(e => {
        ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens uploaden van achtergrond bestand: "${e.message}"`, severity.error);
    });
};

const UnderlayEdit: FC<UnderlayEditProps> = ({ currentSite, editingUnderlay, setEditingUnderlay, onChangeSite }) => {

    const [open, setOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');

    const [selectingTwoPoints, setSelectingTwoPoints] = useState<boolean>(false);
    const [twoPoints, setTwoPoints] = useState<[Point3?, Point3?]>([]);
    const [distance, setDistance] = useState<number>(0);

    const [defaultPosition] = useState<Point3>([0, 0.05, 0]);
    const [defaultRotation] = useState<Point3>([Math.PI / 2, 0, 0]);

    const callbackTwoPoints = useCallback((pt) => {
        if (twoPoints.length === 0 || twoPoints.length === 2) {
            setTwoPoints([pt]);
        } else {
            setTwoPoints([twoPoints[0], pt]);
        }
    }, [twoPoints]);

    useEffect(() => {
        if (pdfUrl !== '') {
            setOpen(true);
        }
    }, [pdfUrl])

    useEffect(() => {
        if (editingUnderlay) {
            ServiceManager.sceneManager.imageTileService.startEdit();
            ServiceManager.sceneManager.modelLoaderService.disableGizmosInAllGroupControls();
        } else {
            ServiceManager.sceneManager.imageTileService.stopEdit();
            ServiceManager.sceneManager.modelLoaderService.enableGizmosInAllGroupControls();
            setTwoPoints([]);
            selectTwoPoints(false);
        }
    }, [editingUnderlay])

    useEffect(() => {
        ServiceManager.sceneManager.imageTileService.pointSelectedCallback = callbackTwoPoints;
    }, [twoPoints, callbackTwoPoints])

    const handleFile = (siteUnderlayImage: File) => {
        if (siteUnderlayImage && siteUnderlayImage.name !== 'empty') {
            const ext = siteUnderlayImage.name.split('.').pop();
            if (ext.toLowerCase() === 'pdf') {
                const url = URL.createObjectURL(siteUnderlayImage);
                setPdfUrl(url);
            } else {
                // load image
                const url = URL.createObjectURL(siteUnderlayImage);
                var img = new Image();
                img.src = url;
                img.onload = function () {
                    const res = this as any;
                    const scale = ServiceManager.sceneManager.imageTileService.computeDefaultScale(res.width);
                    ServiceManager.sceneManager.imageTileService.setImageFileTile(siteUnderlayImage, defaultPosition, defaultRotation, res.width, res.height, scale);
                    const extension = siteUnderlayImage.name.split('.').pop();
                    saveFileAndUnderlayData(currentSite, onChangeSite, defaultPosition, defaultRotation, extension, siteUnderlayImage, res.width, res.height, scale);
                }
            }
        }
    }

    const toggleEdit = (edit: boolean) => {
        setEditingUnderlay(edit)
    }

    const selectTwoPoints = (selecting: boolean) => {
        setSelectingTwoPoints(selecting);
        ServiceManager.sceneManager.imageTileService.selectingTwoPoints(selecting);
    }

    const calculateAndApplyScale = () => {
        const ui = currentSite.underlayImage;
        const scale = ServiceManager.sceneManager.imageTileService.computeScaleOnDimensionAndTwoPoints(twoPoints[0], twoPoints[1], distance, ui.scale)
        ServiceManager.sceneManager.imageTileService.applyScaleOnImageTile(ui.width, ui.height, ui.position, ui.rotation, scale);
        setTwoPoints([]);
        setSelectingTwoPoints(false);
        selectTwoPoints(false);
        setEditingUnderlay(false);
        onChangeSite(currentSite.projectId,
            {
                ...currentSite,
                underlayImage: { ...ui, scale }
            }
        );
    }

    const deleteUnderlay = () => {
        FirestoreService.deleteFileOrFolder(currentSite.underlayImage.path).then(s => {
            ServiceManager.sceneManager.imageTileService.deleteImageTile();
            const cs = { ...currentSite };
            delete cs.underlayImage;
            onChangeSite(currentSite.projectId,
                {
                    ...cs,
                    underlayImage: deleteField() as any
                }
            );
        }).catch(e => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens verwijderen van achtergrond bestand: "${e.message}"`, severity.error);
        });
    }

    return (
        <div className={styles.UnderlayEdit} data-testid="UnderlayEdit">
            {
                !editingUnderlay &&
                <>
                    <input
                        data-testid="FileInput" 
                        accept="image/*,application/pdf"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={(e) => handleFile(e.target.files[0])}
                    />
                    <label htmlFor="raised-button-file">
                        {pdfUrl && <PdfPreview pdfUrl={pdfUrl} open={open} setOpen={setOpen}
                            setImage={(res: { file: File, width: number, height: number }) => {
                                const scale = ServiceManager.sceneManager.imageTileService.computeDefaultScale(res.width);
                                ServiceManager.sceneManager.imageTileService.setImageFileTile(res.file, defaultPosition, defaultRotation, res.width, res.height, scale);
                                saveFileAndUnderlayData(currentSite, onChangeSite, defaultPosition, defaultRotation, 'png', res.file, res.width, res.height, scale);
                            }}></PdfPreview>}
                        <Button color="primary" data-testid="LoadBackgroundBtn" className={styles.underlay} size="small" variant="outlined" component="span">
                            Achtergrond Inladen
                        </Button>
                    </label>
                </>
            }
            {currentSite.underlayImage &&
                <>
                    {
                        editingUnderlay ?
                            <div className={styles.underlay}>
                                <div>
                                    Schaal: {currentSite.underlayImage.scale.toFixed(2)}
                                </div>
                                {
                                    !selectingTwoPoints &&
                                    <Button color="secondary" data-testid="SelectTwoPointsBtn" className={styles.underlay} size="small" variant="outlined" onClick={() => selectTwoPoints(true)}>
                                        Twee Punten Selecteren
                                    </Button>
                                }
                                {
                                    selectingTwoPoints &&
                                    <>
                                        {twoPoints.map((s, index) => (
                                            <div data-testid={'Point-'+index} className={styles.points} key={index}>
                                                X: {s[0].toFixed(2)}, Y: {s[2].toFixed(2)}
                                            </div>
                                        ))}
                                        {
                                            twoPoints.length === 2 &&
                                            <>
                                                <div className={styles.underlay}>
                                                    <TextField 
                                                        data-testid="DistanceInput"
                                                        id="underlay-distance"
                                                        label="Afstand (m)"
                                                        type="number"
                                                        InputProps={{ inputProps: { min: 0, max: 10000 } }}
                                                        fullWidth
                                                        value={distance}
                                                        onChange={(e) => { setDistance(+e.target.value) }}
                                                    />
                                                </div>
                                                {distance !== 0 &&
                                                    <Button color="secondary" data-testid="ComputeScaleBtn" className={styles.underlay} size="small" variant="outlined" onClick={() => calculateAndApplyScale()}>
                                                        Nieuw Schaal Berekenen
                                                    </Button>
                                                }
                                            </>
                                        }
                                    </>
                                }
                                <Button color="secondary" data-testid="DoneBtn" className={styles.underlay} size="small" variant="outlined"
                                    onClick={() => { toggleEdit(false) }}>
                                    Klaar
                                </Button>
                            </div> :
                            <>
                                <Button color="primary" data-testid="EditBackgroundBtn" className={styles.underlay} size="small" variant="outlined"
                                    onClick={() => { toggleEdit(true) }}>
                                    Achtergrond Wijzigen
                                </Button>
                                <Button color="primary" data-testid="DeleteBackgroundBtn" className={styles.underlay} size="small" variant="outlined"
                                    onClick={() => { deleteUnderlay() }}>
                                    Achtergrond Verwijderen
                                </Button>
                            </>

                    }
                </>
            }
        </div>
    )
};

export default UnderlayEdit;